import { ExtractComponentProp } from '@approvalmax/types';
import { Select, Table } from '@approvalmax/ui/src/components';
import { memo, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { linesNamePrefix } from '../ExpenseSection/ExpenseSection.constants';
import { messages } from './GeneralSelector.messages';
import { GeneralSelectorProps } from './GeneralSelector.types';

export const GeneralSelector = memo<GeneralSelectorProps>((props) => {
    const { cellProps, name, items, placeholder = messages.placeholder, ...restProps } = props;

    const { setValue } = useFormContext();
    const fieldName = `${linesNamePrefix}.${cellProps.item.index}.${name}`;

    const enrichedItems = useMemo(() => {
        const fieldValue = cellProps.item[name];

        if (typeof fieldValue !== 'object' || !fieldValue || !('id' in fieldValue)) {
            return items;
        }

        const isExistingItem = items.some(({ id }) => id === fieldValue.id);

        return isExistingItem ? items : [fieldValue, ...items];
    }, [items, cellProps?.item, name]);

    const handleChange = useCallback<ExtractComponentProp<typeof Select<(typeof enrichedItems)[number]>, 'onChange'>>(
        (value, items) => {
            setValue(
                fieldName,
                items.find((item) => item.id === value)
            );
        },
        [setValue, fieldName]
    );

    return (
        <Table.FormCell name={`${fieldName}Id`} {...cellProps}>
            {({ ref, name }) => (
                <Select
                    activatorRef={ref}
                    name={name}
                    itemNameKey='text'
                    autocomplete
                    bordered={false}
                    size='small'
                    items={enrichedItems}
                    onChange={handleChange}
                    placeholder={placeholder}
                    {...restProps}
                />
            )}
        </Table.FormCell>
    );
});

GeneralSelector.displayName = 'GeneralSelector';
